import { useState } from 'react';
import { CollapsibleSection } from '../Marketing/shared';
import { Card, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import {
  useAdTestTags,
  useAddAdTestTag,
  useDeleteAdTestTag,
  useContent,
  useAddContent,
  useLinkedInEngagement,
  useAddLinkedInEngagement,
} from '../../hooks/useMeasurement';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { MeasurementShell } from './measurement/MeasurementShell';
import { Table, THead, Th, Td, Tr, Nil } from '../../components/ui/Table';
import { EmptyState, LoadingState } from '../../components/ui/States';

const PLATFORM_LABELS: Record<string, string> = { google_ads: 'Google Ads', meta_ads: 'Meta Ads' };

export function MeasurementDashboard() {
  return (
    <div className="space-y-8">
      <MeasurementShell />
      {/* Rarely-used manual logs: one click away instead of a third of the page. */}
      <CollapsibleSection title="Manual inputs" description="Campaign tags, published content and LinkedIn logs">
        <AdTestSection />
        <div className="grid gap-6 lg:grid-cols-2">
          <ContentSection />
          <LinkedInSection />
        </div>
      </CollapsibleSection>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ad Test Section
// ─────────────────────────────────────────────────────────────────────────────

function AdTestSection() {
  const { data, isLoading } = useAdTestTags();
  const deleteTag = useDeleteAdTestTag();
  const [showAddModal, setShowAddModal] = useState(false);

  const tags = data?.tags ?? [];

  return (
    <Card>
      <CardHeader
        title="Ad test tags"
        description="Map campaigns to messaging variants and ICP segments"
        action={
          <Button size="sm" variant="outline" onClick={() => setShowAddModal(true)}>
            Add tag
          </Button>
        }
      />
      {isLoading ? (
        <LoadingState />
      ) : tags.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <tr>
                <Th>Platform</Th>
                <Th>Campaign</Th>
                <Th>Ad group</Th>
                <Th>Messaging variant</Th>
                <Th>ICP segment</Th>
                <Th></Th>
              </tr>
            </THead>
            <tbody>
              {tags.map((tag) => (
                <Tr key={tag.id} className="last:border-0">
                  <Td>
                    <Badge variant="default">{PLATFORM_LABELS[tag.platform] ?? tag.platform}</Badge>
                  </Td>
                  <Td className="text-text-primary">
                    {tag.campaignName ?? tag.campaignId}
                  </Td>
                  <Td className="text-text-secondary">
                    {tag.adGroupName ?? tag.adGroupId ?? <Nil />}
                  </Td>
                  <Td className="text-text-primary">{tag.messagingVariant}</Td>
                  <Td className="text-text-primary">{tag.icpSegment}</Td>
                  <Td>
                    <button
                      onClick={() => deleteTag.mutate(tag.id)}
                      className="text-text-tertiary hover:text-error text-xs"
                    >
                      Remove
                    </button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <EmptyState compact title="No ad test tags yet. Map your campaigns to messaging variants and ICP segments." />
      )}
      {showAddModal && <AddAdTestTagModal onClose={() => setShowAddModal(false)} />}
    </Card>
  );
}

function AddAdTestTagModal({ onClose }: { onClose: () => void }) {
  const [platform, setPlatform] = useState<'google_ads' | 'meta_ads'>('google_ads');
  const [campaignId, setCampaignId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [adGroupId, setAdGroupId] = useState('');
  const [messagingVariant, setMessagingVariant] = useState('');
  const [icpSegment, setIcpSegment] = useState('');
  const addTag = useAddAdTestTag();

  const handleSubmit = () => {
    if (!campaignId.trim() || !messagingVariant.trim() || !icpSegment.trim()) return;
    addTag.mutate(
      {
        platform,
        campaignId: campaignId.trim(),
        campaignName: campaignName.trim() || undefined,
        adGroupId: adGroupId.trim() || undefined,
        messagingVariant: messagingVariant.trim(),
        icpSegment: icpSegment.trim(),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Add Ad Test Tag" size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as 'google_ads' | 'meta_ads')}
              className="w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-sm text-text-primary"
            >
              <option value="google_ads">Google Ads</option>
              <option value="meta_ads">Meta Ads</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Campaign ID</label>
            <Input value={campaignId} onChange={(e) => setCampaignId(e.target.value)} placeholder="12345678" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Campaign Name (optional)</label>
            <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Brand awareness - Q2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Ad Group ID (optional)</label>
            <Input value={adGroupId} onChange={(e) => setAdGroupId(e.target.value)} placeholder="87654321" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Messaging variant</label>
            <Input value={messagingVariant} onChange={(e) => setMessagingVariant(e.target.value)} placeholder="ROI-focused" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">ICP segment</label>
            <Input value={icpSegment} onChange={(e) => setIcpSegment(e.target.value)} placeholder="mid-market-manufacturing" />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={addTag.isPending}>Add Tag</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Content Section
// ─────────────────────────────────────────────────────────────────────────────

function ContentSection() {
  const { data, isLoading } = useContent();
  const [showAddModal, setShowAddModal] = useState(false);

  const entries = data?.entries ?? [];

  return (
    <Card>
      <CardHeader
        title="Content published"
        description="Track blog posts, glossary entries, and more"
        action={
          <Button size="sm" variant="outline" onClick={() => setShowAddModal(true)}>
            Log content
          </Button>
        }
      />
      {isLoading ? (
        <LoadingState />
      ) : entries.length > 0 ? (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {entries.slice(0, 15).map((entry) => (
            <div key={entry.id} className="flex items-center justify-between py-2 border-b border-border-light last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-text-primary truncate">{entry.title}</p>
                <p className="text-xs text-text-tertiary">{entry.contentType} &middot; {formatDayMonthYear(entry.publishDate)}</p>
              </div>
              {entry.wordCount && (
                <span className="text-xs text-text-tertiary ml-2">{entry.wordCount} words</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState compact title="No content logged yet." />
      )}
      {showAddModal && <AddContentModal onClose={() => setShowAddModal(false)} />}
    </Card>
  );
}

function AddContentModal({ onClose }: { onClose: () => void }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState('blog');
  const [author, setAuthor] = useState('');
  const [publishDate, setPublishDate] = useState(new Date().toISOString().split('T')[0]!);
  const [wordCount, setWordCount] = useState('');
  const addContent = useAddContent();

  const handleSubmit = () => {
    if (!url.trim() || !title.trim()) return;
    addContent.mutate(
      {
        url: url.trim(),
        title: title.trim(),
        contentType,
        author: author.trim() || undefined,
        publishDate,
        wordCount: wordCount ? parseInt(wordCount) : undefined,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Log Published Content" size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">URL</label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://sequesto.com/blog/..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="How to Respond to an RFP Faster" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Type</label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-sm text-text-primary"
            >
              <option value="blog">Blog Post</option>
              <option value="glossary">Glossary Entry</option>
              <option value="case-study">Case Study</option>
              <option value="comparison">Comparison Page</option>
              <option value="landing-page">Landing Page</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Publish Date</label>
            <Input type="date" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Author</label>
            <Input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Patrick" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Word Count</label>
            <Input type="number" value={wordCount} onChange={(e) => setWordCount(e.target.value)} placeholder="1500" />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={addContent.isPending}>Log Content</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LinkedIn Section
// ─────────────────────────────────────────────────────────────────────────────

function LinkedInSection() {
  const { data, isLoading } = useLinkedInEngagement();
  const [showAddModal, setShowAddModal] = useState(false);

  const entries = data?.entries ?? [];

  return (
    <Card>
      <CardHeader
        title="LinkedIn engagement"
        description="Weekly engagement per person"
        action={
          <Button size="sm" variant="outline" onClick={() => setShowAddModal(true)}>
            Log week
          </Button>
        }
      />
      {isLoading ? (
        <LoadingState />
      ) : entries.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <tr>
                <Th>Person</Th>
                <Th>Week</Th>
                <Th align="right">Posts</Th>
                <Th align="right">Impressions</Th>
                <Th align="right">Likes</Th>
                <Th align="right">Comments</Th>
              </tr>
            </THead>
            <tbody>
              {entries.slice(0, 10).map((entry, i) => (
                <Tr key={i} className="last:border-0">
                  <Td className="pr-3 text-text-primary">{entry.personName}</Td>
                  <Td className="pr-3 text-text-tertiary text-xs">{formatDayMonthYear(entry.weekStart)}</Td>
                  <Td align="right" className="pr-3 text-text-primary">{entry.postsPublished}</Td>
                  <Td align="right" className="pr-3 text-text-primary">
                    {entry.impressions.toLocaleString()}
                  </Td>
                  <Td align="right" className="pr-3 text-text-primary">{entry.likes}</Td>
                  <Td align="right" className="text-text-primary">{entry.comments}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <EmptyState compact title="No engagement data yet." />
      )}
      {showAddModal && <AddLinkedInModal onClose={() => setShowAddModal(false)} />}
    </Card>
  );
}

function AddLinkedInModal({ onClose }: { onClose: () => void }) {
  const [personName, setPersonName] = useState('');
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [postsPublished, setPostsPublished] = useState('0');
  const [impressions, setImpressions] = useState('0');
  const [likes, setLikes] = useState('0');
  const [comments, setComments] = useState('0');
  const [shares, setShares] = useState('0');
  const [profileViews, setProfileViews] = useState('0');
  const addEngagement = useAddLinkedInEngagement();

  const handleSubmit = () => {
    if (!personName.trim()) return;
    addEngagement.mutate(
      {
        personName: personName.trim(),
        weekStart,
        postsPublished: parseInt(postsPublished) || 0,
        impressions: parseInt(impressions) || 0,
        likes: parseInt(likes) || 0,
        comments: parseInt(comments) || 0,
        shares: parseInt(shares) || 0,
        profileViews: parseInt(profileViews) || 0,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Log LinkedIn Engagement" size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Person</label>
            <Input value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="Patrick" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Week Starting</label>
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Posts</label>
            <Input type="number" value={postsPublished} onChange={(e) => setPostsPublished(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Impressions</label>
            <Input type="number" value={impressions} onChange={(e) => setImpressions(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Likes</label>
            <Input type="number" value={likes} onChange={(e) => setLikes(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Comments</label>
            <Input type="number" value={comments} onChange={(e) => setComments(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Shares</label>
            <Input type="number" value={shares} onChange={(e) => setShares(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Profile Views</label>
            <Input type="number" value={profileViews} onChange={(e) => setProfileViews(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={addEngagement.isPending}>Log Engagement</Button>
        </div>
      </div>
    </Modal>
  );
}

function getMonday(d: Date): string {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split('T')[0]!;
}
